import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { createConsumer, Consumer, Subscription } from '@rails/actioncable'
import type { NotificationItem } from '../types/notifications'

// События для страниц, которые держат свою копию списка (/notifications): новая
// строка из канала, прочтение одной, прочтение всех. Шлются только после ответа
// сервера, поэтому слушатель может флипать флаги без проверки.
export type NotificationEvent =
  | { type: 'received'; note: NotificationItem }
  | { type: 'read'; id: string }
  | { type: 'readAll' }

// Колокольчик оповещений + центр истории (задача 27): unreadCount инициализируется
// общим пропсом Inertia (см. types/inertia.d.ts) на стороне вызывающего компонента
// через setUnreadCount() — здесь же живёт live-подписка на NotificationsChannel
// (app/channels/notifications_channel.rb, стрим "notifications:#{user.id}") и загрузка
// среза истории через GET /notifications/feed (NotificationsController#feed).
export const useNotificationsStore = defineStore('notifications', () => {
  const unreadCount = ref(0)
  const items = ref<NotificationItem[]>([])
  const loading = ref(false)
  const listeners = new Set<(event: NotificationEvent) => void>()

  function onEvent(listener: (event: NotificationEvent) => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  }

  function emit(event: NotificationEvent): void {
    // Упавший слушатель не должен ни глушить остальных, ни ронять markRead/markAllRead
    // после того, как состояние уже изменено.
    listeners.forEach((listener) => {
      try {
        listener(event)
      } catch (error) {
        console.error('Слушатель событий оповещений упал', error)
      }
    })
  }

  const hasUnread = computed(() => unreadCount.value > 0)
  const badgeLabel = computed(() => (unreadCount.value > 99 ? '99+' : String(unreadCount.value)))

  function setUnreadCount(count: number): void {
    unreadCount.value = count
  }

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
  }

  // Массовое прочтение: серверный bulk (NotificationsController#read_all) +
  // синхронный сброс счётчика и флагов. false — сервер отказал или сети нет;
  // тогда ничего не трогаем, чтобы экран не показал ложное «прочитано».
  async function markAllRead(): Promise<boolean> {
    try {
      const response = await fetch('/notifications/read_all', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrfToken() },
      })
      if (!response.ok) return false
    } catch {
      return false
    }

    unreadCount.value = 0
    items.value.forEach((note) => {
      note.read = true
    })
    emit({ type: 'readAll' })
    return true
  }

  // Срез для выпадашки колокольчика: только НЕПРОЧИТАННЫЕ (feed?unread=true).
  // Прочитанные пользователь уже видел — полная история живёт на странице
  // /notifications. Без навигации Inertia — обычный JSON-fetch порции по limit.
  async function loadNotifications(limit = 10): Promise<void> {
    loading.value = true
    try {
      const response = await fetch(`/notifications/feed?unread=true&limit=${limit}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return

      const payload = (await response.json()) as { notifications: NotificationItem[] }
      items.value = payload.notifications
    } finally {
      loading.value = false
    }
  }

  // Помечает уведомление прочитанным (NotificationsController#read) и синхронно
  // уменьшает счётчик. false — сервер отказал или сети нет.
  async function markRead(id: string): Promise<boolean> {
    const item = items.value.find((note) => note.id === id)
    if (item?.read) return true

    try {
      const response = await fetch(`/notifications/${id}/read`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrfToken() },
      })
      if (!response.ok) return false
    } catch {
      return false
    }

    if (item) item.read = true
    unreadCount.value = Math.max(0, unreadCount.value - 1)
    emit({ type: 'read', id })
    return true
  }

  let consumer: Consumer | null = null
  let subscription: Subscription | null = null

  // Новое оповещение из NotificationsChannel добавляется в начало истории
  // (список отсортирован newest_first) и инкрементит счётчик непрочитанных.
  function onReceived(payload: NotificationItem): void {
    items.value = [{ ...payload, read: false }, ...items.value]
    unreadCount.value += 1
    emit({ type: 'received', note: items.value[0] })
  }

  function subscribe(): void {
    unsubscribe()
    consumer ??= createConsumer()
    subscription = consumer.subscriptions.create({ channel: 'NotificationsChannel' }, { received: onReceived })
  }

  function unsubscribe(): void {
    subscription?.unsubscribe()
    subscription = null
  }

  return {
    unreadCount,
    items,
    loading,
    hasUnread,
    badgeLabel,
    onEvent,
    setUnreadCount,
    markAllRead,
    loadNotifications,
    markRead,
    subscribe,
    unsubscribe,
  }
})
